import type { LocationQueryValue, RouteLocationRaw, RouteParamsRaw } from 'vue-router';

import { ApiError } from '@/core/lib/ApiUtils';
import { RouteName } from '@/core/router/RouteName';
import { AuthenticationError } from '@/plugins/authentication/lib/Authentication';

export class RouteError extends Error {}
export class NotFoundRouteError extends RouteError {}
export class AccessDeniedRouteError extends RouteError {}

export const WILDCARD_PATH = '/:pathMatch(.*)*';

export function convertMaybeErrorToRouteError(maybeError: unknown): RouteError {
  // Api errors
  if (maybeError instanceof ApiError) {
    if (maybeError.statusCode === 404) {
      return new NotFoundRouteError(maybeError.message);
    } else if (maybeError.statusCode === 401 || maybeError.statusCode === 403) {
      return new AccessDeniedRouteError(maybeError.message);
    } else {
      return new RouteError(maybeError.message);
    }
  }

  // Auth errors
  if (maybeError instanceof AuthenticationError) {
    return new AccessDeniedRouteError(maybeError.message);
  }

  // Other possible cases
  let msg: string | undefined = undefined;

  if (maybeError instanceof Error) {
    msg = maybeError.message;
  } else if (typeof maybeError === 'string') {
    msg = maybeError;
  }

  return new RouteError(msg);
}

export function getRouteNameFromMaybeError(maybeError: unknown): RouteLocationRaw {
  if (maybeError instanceof AccessDeniedRouteError) {
    return { name: RouteName.ERROR_ACCESS_DENIED };
  }

  if (maybeError instanceof NotFoundRouteError) {
    return { name: RouteName.ERROR_NOT_FOUND };
  }

  return { name: RouteName.ERROR };
}

export function ensureQueryParamArray(val: LocationQueryValue | LocationQueryValue[]): LocationQueryValue[] {
  if (typeof val === 'string' && val.length > 0) {
    return [val];
  }

  if (Array.isArray(val)) {
    return val;
  }

  return [];
}

export function cleanQueryParams(queryParams: RouteParamsRaw): RouteParamsRaw {
  return Object.entries(queryParams).reduce<RouteParamsRaw>((acc, [key, val]) => {
    if (val === undefined || val === null || (Array.isArray(val) && val.length === 0)) {
      return acc;
    }

    if (Array.isArray(val) && val.length === 1) {
      acc[key] = val.at(0);
    } else {
      acc[key] = val;
    }

    return acc;
  }, {});
}
